// What we store and what each optional provider sets. Data only: the cookie
// panel and the Cookie Policy page both render from this, so they cannot drift
// apart. Every non-essential name seen in the browser check must be listed here
// (docs/features/cookie-consent/SPEC.md section 2). Changing a provider, purpose or
// category means bumping CONSENT_VERSION in consentRecord.js.

export const ESSENTIAL = [
  {
    names: ['token', 'pathbuddy_access_token', 'pathbuddy_refresh_token', 'user', 'pathbuddy_user', 'pathbuddy_session_started_at'],
    where: 'localStorage or sessionStorage',
    purpose: 'Keeps you signed in.',
    duration: 'Until you sign out, or 24 hours',
  },
  {
    names: ['pathbuddy_remember', 'pathbuddy_remember_me', 'pathbuddy_user_email'],
    where: 'localStorage',
    purpose: 'Remembers you on this device when you ask it to ("Remember me").',
    duration: 'Until you sign out or clear it',
  },
  {
    names: ['pathbuddy_theme'],
    where: 'localStorage',
    purpose: 'Remembers the colour scheme you chose: light, dark or black.',
    duration: 'Until you change it',
  },
  {
    names: ['google_oauth_state', 'linkedin_oauth_state', 'returnTo'],
    where: 'sessionStorage',
    purpose: 'Makes signing in with Google or LinkedIn secure.',
    duration: 'This browser tab',
  },
  {
    names: ['pending_join_code'],
    where: 'sessionStorage',
    purpose: 'Holds a school join code until you sign in.',
    duration: 'This browser tab',
  },
  {
    names: ['pending_checkout_tier', 'pending_checkout_exam', 'pending_checkout_package', 'pending_checkout_subject'],
    where: 'sessionStorage',
    purpose: 'Completes a purchase you started.',
    duration: 'This browser tab',
  },
  {
    names: ['Practice progress and settings, for example completed_*_quizzes, *_paper_scores, *_workspace_target, user_primary_subject, chat-reasoning-mode'],
    where: 'localStorage',
    purpose: 'Saves your practice progress and the settings you pick.',
    duration: 'Until you clear it',
  },
  {
    names: ['ivori_admin_*, ivori_certadmin_* (staff only)'],
    where: 'localStorage or sessionStorage',
    purpose: 'Staff sign-in. Not used by students.',
    duration: 'Until staff sign out',
  },
  {
    names: ['ivori_cookie_consent'],
    where: 'localStorage',
    purpose: 'Remembers your cookie choice.',
    duration: '6 months',
  },
];

export const OPTIONAL = [
  {
    id: 'analytics',
    title: 'Analytics',
    purpose: 'Counts visits and shows which pages are used and how fast they load, so we can improve the site.',
    providers: [
      {
        name: 'Google Analytics 4 (Google)',
        detail: 'Measurement ID G-BCYEXGRWQD',
        items: [
          { name: '_ga', party: 'First party, set by Google\'s script', duration: 'Up to 2 years (Chrome limits this to 400 days)' },
          { name: '_ga_BCYEXGRWQD', party: 'First party, set by Google\'s script', duration: 'Up to 2 years (Chrome limits this to 400 days)' },
        ],
        policyUrls: [
          { label: 'Google privacy policy', href: 'https://policies.google.com/privacy' },
          { label: 'How Google uses data from sites that use its services', href: 'https://policies.google.com/technologies/partner-sites' },
        ],
      },
      {
        name: 'IVORI performance measurement',
        detail: 'Sent to our own server',
        items: [{ name: 'None stored', party: 'Our own server', duration: 'Not applicable' }],
        policyUrls: [],
      },
    ],
  },
  {
    id: 'recording',
    title: 'Session recording',
    purpose: 'Records how pages are used (clicks, scrolling and mouse movement) so we can find and fix problems.',
    providers: [
      {
        name: 'Microsoft Clarity (Microsoft)',
        detail: 'Project uffnt2icj1',
        items: [
          { name: '_clck', party: 'First party, set by Microsoft\'s script', duration: '1 year' },
          { name: '_clsk', party: 'First party, set by Microsoft\'s script', duration: '1 day' },
          { name: 'CLID', party: 'Third party (clarity.ms)', duration: '1 year' },
          { name: 'MUID', party: 'Third party (Microsoft)', duration: '1 year' },
          { name: 'ANONCHK', party: 'Third party (Microsoft)', duration: '10 minutes' },
          { name: 'SM', party: 'Third party (Microsoft)', duration: 'Session' },
          { name: 'MR', party: 'Third party (Microsoft)', duration: '7 days' },
        ],
        policyUrls: [{ label: 'Microsoft privacy statement', href: 'https://privacy.microsoft.com/privacystatement' }],
      },
    ],
  },
  {
    id: 'advertising',
    title: 'Advertising',
    purpose: 'Tells us whether our adverts on other sites led to a sign-up or purchase. With Shown.io, it may also be used to show our adverts to people who have visited this site. We never use it to personalise adverts with Google.',
    providers: [
      {
        name: 'Google Ads conversion tracking (Google)',
        detail: 'Tag AW-18348840584',
        items: [
          { name: '_gcl_au', party: 'First party, set by Google\'s script', duration: '90 days' },
          { name: 'Google cookies on doubleclick.net or google.com, for example IDE and test_cookie', party: 'Third party', duration: 'Up to 13 months' },
        ],
        policyUrls: [
          { label: 'Google privacy policy', href: 'https://policies.google.com/privacy' },
          { label: 'How Google uses data for advertising', href: 'https://policies.google.com/technologies/ads' },
        ],
      },
      {
        name: 'Shown.io pixel',
        detail: 'Pixel eK3DDQy53n',
        items: [{ name: 'As set by Shown.io. See its own information for the names and lifetimes.', party: 'First or third party', duration: 'Set by Shown.io' }],
        policyUrls: [{ label: 'Shown.io website', href: 'https://shown.io' }],
      },
    ],
  },
];

/** Every optional cookie name we can name, for tests and the browser check. */
export function listedOptionalNames() {
  return OPTIONAL.flatMap((c) => c.providers.flatMap((p) => p.items.map((i) => i.name)));
}
